import { useIntl } from 'react-intl';
import { NavLink, useLocation } from 'react-router-dom';
import { getNavItems, NavItem, SubNavItem } from './NavItems';
import { cn } from 'utils/classNamesTailwind';
import { useContext, useEffect, useRef, useState } from 'react';
import { BETA_CUSTOMER_FLAG, TenantConfigContext } from 'context/TenantConfigContext';
import { TbIcon } from 'components/Figma';
import { Tooltip } from 'components/Tooltip';

interface SidebarProps {
  sidebarCollapsed: boolean;
  className?: string;
}

export function Sidebar({ sidebarCollapsed, className = '' }: SidebarProps) {
  const location = useLocation();
  const intl = useIntl();
  const { isSuperAdmin, isAdminView, isSupportAdmin, featureFlags } = useContext(TenantConfigContext);

  const [openMenuItemId, setOpenMenuItemId] = useState<string | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const openMenu = (itemId: string) => {
    clearCloseTimeout();
    setOpenMenuItemId(itemId);
  };

  const scheduleCloseMenu = (itemId: string) => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setOpenMenuItemId(prev => (prev === itemId ? null : prev));
    }, 200);
  };

  useEffect(() => {
    setOpenMenuItemId(null);
  }, [location.pathname]);

  useEffect(() => clearCloseTimeout, []);

  const dynamicNavItems: NavItem[] = getNavItems({
    isSuperAdmin,
    isAdminView,
    isSupportAdmin,
    isBeta: featureFlags.includes(BETA_CUSTOMER_FLAG),
  });

  const sidebarWidth = sidebarCollapsed ? 'w-20' : 'w-64';

  const getActiveSubItemId = (item: NavItem): string | undefined => {
    if (!item.subItems?.length || !location.pathname.startsWith(item.route)) {
      return undefined;
    }
    const sortedByRouteLength = [...item.subItems].sort((a, b) => b.route.length - a.route.length);
    const bestMatch = sortedByRouteLength.find(subItem => location.pathname.startsWith(subItem.route));
    return (bestMatch ?? item.subItems[0]).id;
  };

  const renderSubItemRow = (subItem: SubNavItem, isActive: boolean, onNavigate?: () => void, compact?: boolean) => (
    <NavLink
      key={subItem.id}
      to={subItem.route + location.search}
      onClick={onNavigate}
      className={cn(
        'flex items-center text-sm gap-3 transition-colors relative rounded-md py-3 px-3',
        'text-ui-navText hover:text-ui-navTextHover',
        'hover:bg-interactive-selected-nav-hover',
        {
          'bg-interactive-selected-nav-default font-bold': isActive,
          'font-normal hover:font-semibold': !isActive,
          'min-w-[250px]': compact,
          'w-full': !compact,
        }
      )}
    >
      <span
        className={cn('flex items-center justify-center w-4 h-4 flex-shrink-0 gap-3 pr-3', {
          'pl-5': !compact,
          'pl-3': compact,
        })}
      >
        {isActive && <TbIcon name="SubArrowRight" size="lg" />}
      </span>
      <span className="truncate">
        {intl.formatMessage({
          id: subItem.id,
          defaultMessage: subItem.label,
        })}
      </span>
    </NavLink>
  );

  return (
    <aside
      className={cn(
        'sidebar-height sticky top-0 z-50 flex flex-col transition-all duration-300 text-ash-900 px-2 box-border bg-background-default',
        sidebarCollapsed ? 'items-center' : 'items-stretch',
        sidebarWidth,
        className
      )}
    >
      <div
        className={`flex items-center h-20 px-4 -mt-1 bg-background-default ${sidebarCollapsed ? 'justify-center' : ''}`}
      >
        <div className="flex items-center gap-3">
          <img src="/tradebyte/tb360/tb-360-icon.svg" alt="TB.360" className="w-8 h-10" />
          {!sidebarCollapsed && <span className="text-ash-900 font-bold text-2xl">TB.360</span>}
        </div>
      </div>

      <nav className={cn('flex-1 py-6', sidebarCollapsed ? '' : 'w-full')}>
        <ul className="space-y-2 w-full">
          {dynamicNavItems
            .filter(item => item.show)
            .map(item => {
              const hasSubItems = !!item.subItems?.length;
              const activeSubItemId = getActiveSubItemId(item);
              const isSectionActive = hasSubItems && location.pathname.startsWith(item.route);
              const isExpandedInline = !sidebarCollapsed && isSectionActive;

              return (
                <li
                  key={item.id}
                  className="relative group w-full"
                  onMouseEnter={() => {
                    if (sidebarCollapsed && hasSubItems) {
                      openMenu(item.id);
                    }
                  }}
                  onMouseLeave={() => {
                    if (sidebarCollapsed && hasSubItems) {
                      scheduleCloseMenu(item.id);
                    }
                  }}
                >
                  {(() => {
                    const targetRoute =
                      sidebarCollapsed && hasSubItems && item.subItems ? item.subItems[0].route : item.route;
                    const navLink = (
                      <NavLink
                        to={targetRoute + location.search}
                        id={item.id}
                        className={({ isActive: navLinkIsActive }) => {
                          const isActive = hasSubItems ? isSectionActive : navLinkIsActive;
                          const showAsActiveBg = isActive && (sidebarCollapsed || !hasSubItems);
                          return cn(
                            'flex items-center gap-3 text-sm transition-colors relative rounded-md',
                            'text-ui-navText hover:text-ui-navTextHover',
                            'hover:bg-interactive-selected-nav-hover active:bg-interactive-selected-nav-default',
                            {
                              'bg-interactive-selected-nav-default': showAsActiveBg,
                              'justify-center items-center w-14 h-14 min-w-0': sidebarCollapsed,
                              'py-4 px-3 w-full min-w-0': !sidebarCollapsed,
                            }
                          );
                        }}
                      >
                        {({ isActive: navLinkIsActive }) => {
                          const isActive = hasSubItems ? isSectionActive : navLinkIsActive;
                          return (
                            <>
                              <span
                                className={cn(
                                  'flex items-center justify-center w-6 h-6 flex-shrink-0 transition-colors group-hover:text-ui-navIconActive',
                                  isActive ? 'text-ui-navIconActive' : 'text-ui-navIcon'
                                )}
                              >
                                {item.icon}
                              </span>
                              {!sidebarCollapsed && (
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                  <span
                                    className={cn('truncate text-ui-navText', {
                                      'font-bold': isActive,
                                      'font-normal group-hover:font-semibold': !isActive,
                                    })}
                                  >
                                    {intl.formatMessage({
                                      id: item.id,
                                      defaultMessage: item.label,
                                    })}
                                  </span>
                                  {item.badge}
                                  {item.notificationBadge}
                                </div>
                              )}
                              {sidebarCollapsed && item.notificationBadge && (
                                <div className="absolute -top-1.5 -right-1.5">{item.notificationBadge}</div>
                              )}
                            </>
                          );
                        }}
                      </NavLink>
                    );

                    if (!sidebarCollapsed || hasSubItems) {
                      return navLink;
                    }

                    return (
                      <Tooltip
                        tooltipContent={intl.formatMessage({
                          id: item.id,
                          defaultMessage: item.label,
                        })}
                        placement="right"
                        arrow
                        delay="delay-100"
                        className="w-full"
                      >
                        {navLink}
                      </Tooltip>
                    );
                  })()}

                  {isExpandedInline && (
                    <ul className="mt-1 space-y-1 w-full">
                      {item.subItems?.map(subItem => renderSubItemRow(subItem, subItem.id === activeSubItemId))}
                    </ul>
                  )}

                  {sidebarCollapsed && hasSubItems && (
                    <div
                      className={cn(
                        'absolute left-full top-0 ml-3 py-0.5 bg-white text-sm shadow-xl transition-opacity duration-200 z-50 border border-border-default rounded-md px-0.5',
                        openMenuItemId === item.id ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
                      )}
                    >
                      <div className="px-3 py-2 font-bold text-ui-navText truncate border-b border-border-default">
                        {intl.formatMessage({
                          id: item.id,
                          defaultMessage: item.label,
                        })}
                      </div>
                      <ul>
                        {item.subItems?.map(subItem =>
                          renderSubItemRow(subItem, subItem.id === activeSubItemId, () => setOpenMenuItemId(null), true)
                        )}
                      </ul>
                    </div>
                  )}
                </li>
              );
            })}
        </ul>
      </nav>
    </aside>
  );
}
